import React from 'react';
import { rem } from 'polished';
import { Global, css } from '@emotion/react';
import { normalize } from 'polished';
import { text, colors, mixins } from './theme/default';

const SctoGroteskAThin = '/fonts/Scto_Grotesk_A_Thin.woff';
const SctoGroteskARegular = '/fonts/Scto_Grotesk_A_Regular.woff';
const SctoGroteskAMedium = '/fonts/Scto_Grotesk_A_Medium.woff';
const SctoGroteskABold = '/fonts/Scto_Grotesk_A_Bold.woff';
const SctoGroteskABlack = '/fonts/Scto_Grotesk_A_Black.woff';

const GlobalStyles = () => {
  return (
    <Global
      styles={(theme) => css`
        ${normalize()};

        *,
        ::after,
        ::before {
          box-sizing: inherit;
        }

        @font-face {
          font-family: 'Scto Grotesk A';
          src: local('Scto Grotesk A'), url(${SctoGroteskAThin}) format('woff');
          font-weight: 100;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Scto Grotesk A';
          src: local('Scto Grotesk A'),
            url(${SctoGroteskARegular}) format('woff');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Scto Grotesk A';
          src: local('Scto Grotesk A'),
            url(${SctoGroteskAMedium}) format('woff');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Scto Grotesk A';
          src: local('Scto Grotesk A'), url(${SctoGroteskABold}) format('woff');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Scto Grotesk A';
          src: local('Scto Grotesk A'), url(${SctoGroteskABlack}) format('woff');
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }

        html {
          box-sizing: border-box;
        }

        html,
        body {
          background-color: #fff;
        }

        body {
          font-family: 'Scto Grotesk A', sans-serif;
          ${text.p3};
          color: ${colors.grey900};

          &.nav--open {
            overflow: hidden;
            height: 100dvh;
          }
        }

        h1 {
          color: ${colors.primary800};
        }

        h2,
        h3,
        h4,
        h5,
        h6,
        a {
          color: ${colors.blue900};
        }

        a {
          text-decoration: none;
        }

        button:focus,
        a:focus {
          outline: none;
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }

        figure {
          margin: 0;
        }

        #gatsby-focus-wrapper {
          display: flex;
          min-height: 100vh;
          flex-direction: column;
        }

        .m-richtext {
          a {
            text-decoration: underline;
            text-underline-offset: ${rem(4)};

            &:hover,
            &:focus {
              text-decoration: none;
            }
          }

          p {
            white-space: break-spaces;

            + p {
              padding-top: ${rem(16)};
            }
          }

          ul,
          ol {
            margin: 0;
            padding-left: ${rem(32)};

            ul,
            ol {
              margin-top: ${rem(10)};
            }

            li {
              + li {
                margin-top: ${rem(10)};
              }

              p + p {
                padding-top: 0;
              }
            }
          }

          .m-publication {
            height: auto;
            margin: ${rem(24)} auto;
            min-width: 0;

            &:last-child {
              margin-bottom: 0;
            }

            ${mixins.responsiveStyles({
              'max-width': {
                zero: '100%',
                small: `calc(50% - ${rem(24)} / 2)`,
                medium: `calc(${100 / 3}% - ${rem(48)} / 3)`,
                wide: `calc(25% - ${rem(72)} / 4)`,
                ultra: 297,
              },
            })}
          }

          &--multi-item {
            & > div {
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              gap: ${rem(24)};

              .m-publication {
                margin: 0;
                max-width: none !important;

                ${mixins.responsiveStyles({
                  'flex-basis': {
                    zero: '100%',
                    small: `calc(50% - ${rem(24)} / 2)`,
                    medium: `calc(${100 / 3}% - ${rem(48)} / 3)`,
                    wide: `calc(25% - ${rem(72)} / 4)`,
                  },
                })}
              }
            }
          }

          &--nested {
            .m-genericteasermodule,
            .m-calltoaction,
            .m-faq,
            .m-cardgrid,
            .m-gallery,
            .m-eventsteasermodule,
            .m-customfrontendelement {
              & > div {
                width: 100%;
                margin: 0;

                & > div {
                  flex-basis: 100%;
                  width: 100%;
                  margin: 0;
                  left: 0;
                }
              }
            }

            &.m-richtext--multi-item {
              & > div {
                .m-publication {
                  ${mixins.responsiveStyles({
                    'flex-basis': {
                      zero: '100%',
                      small: `calc(50% - ${rem(24)} / 2)`,
                      medium: `calc(${100 / 3}% - ${rem(48)} / 3)`,
                    },
                  })}
                }
              }
            }
          }
        }

        ${theme?.stylesExtension?.global}
      `}
    />
  );
};

export default GlobalStyles;
